import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Target, Users, Lightbulb, GitBranch, Sparkles, LayoutDashboard, Folder, DollarSign, Eye } from 'lucide-react';

const HelpPage = () => {
  return (
    <div className="min-h-screen bg-bg-base py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-text-secondary hover:text-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          Back to Dashboard
        </Link>

        <div className="bg-bg-surface rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <h1 className="font-display text-4xl font-bold text-text-primary mb-3">How to Use Daypop</h1>
          <p className="text-text-secondary text-lg mb-10">
            Everything you need to know to get the most out of your personal growth journal.
          </p>

          <div className="space-y-12">

            {/* Getting Started */}
            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">Getting Started</h2>
              <div className="bg-gold-light/20 rounded-xl p-6 border border-gold/20">
                <ol className="space-y-3 text-text-secondary">
                  <li className="flex gap-3">
                    <span className="text-gold font-bold shrink-0">1.</span>
                    <span><strong className="text-text-primary">Sign in</strong> with your Google account. Your data is private and encrypted.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold font-bold shrink-0">2.</span>
                    <span><strong className="text-text-primary">Complete onboarding</strong> — set your name, start a chapter, and optionally add your first goal and contact.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold font-bold shrink-0">3.</span>
                    <span><strong className="text-text-primary">Start logging</strong> — open the Log page and create your first entry. That&apos;s it. Everything else builds from here.</span>
                  </li>
                </ol>
              </div>
            </section>

            {/* Dashboard */}
            <FeatureSection
              icon={LayoutDashboard}
              iconBg="bg-gold-light"
              iconColor="text-gold"
              title="Dashboard"
              description="Your home base. See a quick summary of your recent activity, stats, and a daily echo — a random lesson surfaced from your history to keep past insights alive."
              tips={[
                'Check it daily for a pulse on your journey.',
                'The Daily Echo changes each day — it picks a lesson you logged and reminds you.',
                'Stats show your total entries, lessons, goals hit, and current streak.',
              ]}
            />

            {/* Log */}
            <FeatureSection
              icon={BookOpen}
              iconBg="bg-gold-light"
              iconColor="text-gold"
              title="Log"
              description="The core of Daypop. Record what happened each day using six entry types, each designed for a different kind of moment."
              tips={[
                'Win — something that went well. Celebrate progress, no matter how small.',
                'Setback — something that didn\'t go as planned. Honest records build self-awareness.',
                'Meeting — a conversation worth remembering. Capture who, what, and any follow-ups.',
                'Idea — a spark or thought you don\'t want to lose. You can promote ideas to goals later.',
                'Milestone — a significant achievement or turning point.',
                'Note — anything else. Context, observations, feelings.',
              ]}
            />

            {/* Goals */}
            <FeatureSection
              icon={Target}
              iconBg="bg-sage-light"
              iconColor="text-sage"
              title="Goals"
              description="Set meaningful goals and break them into steps. Track progress as you complete each step — the progress bar updates automatically."
              tips={[
                'Each goal can have multiple steps. Check them off as you go.',
                'Progress percentage is calculated from completed steps.',
                'Set a target date to keep yourself accountable.',
                'Goals connect to your chapters — they give structure to your journey.',
              ]}
            />

            {/* Orbit */}
            <FeatureSection
              icon={Users}
              iconBg="bg-teal-light"
              iconColor="text-teal"
              title="Orbit"
              description="Your personal network map. Track the people who matter — mentors, collaborators, investors, friends — with context about each relationship."
              tips={[
                'Add where you met, what you discussed, and when to follow up.',
                'Connect with other Daypop users by searching their username.',
                'Set follow-up dates so important relationships don\'t go cold.',
                'Tag contacts by relevance: collaborator, mentor, investor, friend, etc.',
              ]}
            />

            {/* Lessons */}
            <FeatureSection
              icon={Lightbulb}
              iconBg="bg-amber-light"
              iconColor="text-amber"
              title="Lessons"
              description="Extract insights from your experiences. When something teaches you a lesson — from a win or a failure — capture it here so you can find it again."
              tips={[
                'Lessons can be linked to log entries for context.',
                'Categorize them so they\'re easy to search later.',
                'Mark lessons as applied when you\'ve actually used the insight.',
                'The Dashboard shows a random lesson daily to keep your wisdom alive.',
              ]}
            />

            {/* Ideas */}
            <FeatureSection
              icon={Sparkles}
              iconBg="bg-lavender-light"
              iconColor="text-lavender"
              title="Ideas"
              description="A scratch pad for ideas that aren't ready to become goals yet. Capture them quickly so nothing gets lost."
              tips={[
                'Ideas start as simple notes with a category.',
                'When an idea matures, promote it to a goal with one click.',
                'Use statuses like backlog, exploring, or promoted to organize them.',
              ]}
            />

            {/* Decisions */}
            <FeatureSection
              icon={GitBranch}
              iconBg="bg-lavender-light"
              iconColor="text-lavender"
              title="Decisions"
              description="Log important decisions with context about why you made them. When you look back months later, you'll remember not just what you chose, but why."
              tips={[
                'Record what you decided and why — the reasoning matters most.',
                'Add outcomes later once you see how it played out.',
                'Decisions are tied to dates so you can see them on your timeline.',
              ]}
            />

            {/* Journey */}
            <FeatureSection
              icon={Folder}
              iconBg="bg-sage-light"
              iconColor="text-sage"
              title="Journey & Chapters"
              description="Organize your life into chapters — phases like a new job, a move, a project, or a personal challenge. Chapters give your timeline structure."
              tips={[
                'Create a new chapter when you start a new phase of life or work.',
                'Each chapter has a start and optional end date.',
                'The Journey page shows all your chapters, entries, and how they connect.',
              ]}
            />

            {/* Money */}
            <FeatureSection
              icon={DollarSign}
              iconBg="bg-amber-light"
              iconColor="text-amber"
              title="Money Snapshots"
              description="Simple monthly financial tracking. Record revenue, key expenses, and notable financial events to keep a high-level view of your financial journey."
              tips={[
                'One snapshot per month — keep it simple.',
                'Not a full accounting tool, just enough to spot trends.',
                'Great for freelancers, founders, and anyone tracking financial progress alongside life goals.',
              ]}
            />

            {/* Public Profile / Showcase */}
            <FeatureSection
              icon={Eye}
              iconBg="bg-teal-light"
              iconColor="text-teal"
              title="Public Showcase"
              description="Optionally share your journey with others. When enabled, other Daypop users can see your stats and selected entries at your public profile URL."
              tips={[
                'Toggle your journey visibility in Settings > Privacy.',
                'You control what\'s visible — only entries marked for showcase are shown.',
                'Your profile URL is daypop.online/u/your-username.',
                'Full entry content is never shared — only titles and metadata.',
              ]}
            />

            {/* Settings */}
            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">Settings</h2>
              <p className="text-text-secondary leading-relaxed mb-4">
                Manage your profile, privacy, and preferences. You can update your display name, username, tagline,
                and toggle whether your journey is publicly visible.
              </p>
            </section>

            {/* Contact / Feedback */}
            <section className="bg-gold-light/20 rounded-xl p-8 border border-gold/20 text-center">
              <h2 className="font-display text-2xl font-bold text-text-primary mb-3">Questions or Feedback?</h2>
              <p className="text-text-secondary mb-4">
                We&apos;re building Daypop to be genuinely useful. If something is confusing, broken, or missing — we want to know.
              </p>
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
              >
                Share Feedback
              </Link>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureSection = ({ icon: Icon, iconBg, iconColor, title, description, tips }) => (
  <section>
    <div className="flex items-center gap-4 mb-4">
      <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center shrink-0`}>
        <Icon size={20} className={iconColor} />
      </div>
      <h2 className="font-display text-2xl font-bold text-text-primary">{title}</h2>
    </div>
    <p className="text-text-secondary leading-relaxed mb-4">{description}</p>
    {tips && tips.length > 0 && (
      <ul className="space-y-2">
        {tips.map((tip, i) => (
          <li key={i} className="flex items-start gap-3 text-text-secondary text-sm">
            <span className="text-gold font-bold mt-0.5 shrink-0">-</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default HelpPage;
